import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { FormikProps, withFormik } from 'formik';

export type LoginFormValues = {
    email: string,
    password: string
}

export type LoginFormProps = {
    loading: boolean;
    validationSchema: any ;
    handleSubmit: (values: LoginFormValues) => Promise<void>;
}

const InnerLoginForm: React.FC<LoginFormProps & FormikProps<LoginFormValues>> = (props) => {
    const {
        loading,
        getFieldProps,
        errors,
        handleSubmit, 
        touched,
        isSubmitting,
        isValid,
        status
    } = props;

    return (
        <form
            className="form w-100"
            onSubmit={handleSubmit}
            noValidate
            id="kt_login_signin_form"
        >
            {status ? (
                <div className="mb-lg-15 alert alert-danger">
                    <div className="alert-text font-weight-bold">{status}</div>
                </div>
            ) : null}

            {/* begin::Form group */}
            <div className="fv-row mb-10">
                <label className="form-label fs-6 fw-bolder text-dark">Email</label>
                <input
                    placeholder="Email"
                    {...getFieldProps('email')}
                    className={clsx(
                        'form-control form-control-lg form-control-solid',
                        {'is-invalid': touched.email && errors.email},
                        {
                            'is-valid': touched.email && !errors.email,
                        },
                    )}
                    type="email"
                    name="email"
                    autoComplete="email"
                />
                {touched.email && errors.email && (
                    <div className="fv-plugins-message-container">
                        <span role="alert">{errors.email}</span>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className="fv-row mb-10">
                <div className="d-flex justify-content-between mt-n5">
                    <div className="d-flex flex-stack mb-2">
                        {/* begin::Label */}
                        <label className="form-label fw-bolder text-dark fs-6 mb-0">Password</label>
                        {/* end::Label */}
                        {/* begin::Link */}
                        <Link
                            to="/auth/forgot-password"
                            className="link-primary fs-6 fw-bolder"
                            style={{marginLeft: '5px'}}
                        >
                            Forgot Password ?
                        </Link>
                        {/* end::Link */}
                    </div>
                </div>
                <input
                    type="password"
                    autoComplete="current-password"
                    {...getFieldProps('password')}
                    className={clsx(
                        'form-control form-control-lg form-control-solid',
                        {
                            'is-invalid': touched.password && errors.password,
                        },
                        {
                            'is-valid': touched.password && !errors.password,
                        },
                    )}
                />
                {touched.password && errors.password && (
                    <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                            <span role="alert">{errors.password}</span>
                        </div>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Action */}
            <div className="text-center">
                <button
                    type="submit"
                    id="kt_sign_in_submit"
                    className="btn btn-lg btn-primary w-100 mb-5"
                    disabled={isSubmitting || !isValid}
                >
                    {!loading && <span className="indicator-label">Continue</span>}
                    {loading && (
                        <span className="indicator-progress" style={{display: 'block'}}>
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2" />
                        </span>
                    )}
                </button>
            </div>
            {/* end::Action */}
        </form>
    );
};

export const LoginForm = withFormik<LoginFormProps, LoginFormValues>({
    handleSubmit: async (values, { props, setStatus, setSubmitting}) => {
        try {
            await props.handleSubmit(values);
        } catch (error: any) {
            setStatus(error?.response?.data?.message || error?.message || 'Unknown error');
            setSubmitting(false);
        }
    },
})(InnerLoginForm);
